import { useCallback, useEffect, useRef, useState } from 'react';
import { apiInvestigados, type AchadoEstadual, type BuscaNosEstados, type Investigado } from '../lib/api';
import { usarEhMaster } from '../lib/papel.ts';
import { Traco } from './ImoveisDoCaso';
import '../estilos/imoveis-2026.css';

/**
 * ⭐⭐ **OS DIÁRIOS DOS ESTADOS, NA QUALIFICAÇÃO DA PARTE — 19/09/2026 (PRD §48.2).**
 *
 * > *"pode seguir pelos estados de porta aberta"* — Richard, 19/09/2026.
 *
 * Irmã da faixa do Diário da União, e com uma diferença que a tela precisa dizer: **a
 * busca é do estado**, responde em segundos, e **cada estado alcança um período
 * diferente** — Mato Grosso chega a 1967, o Paraná a 2009. Prometer "o passado" sem
 * dizer desde quando seria afirmar o que não se sabe.
 *
 * ⛔ **Apontamento, nunca lançamento**, como no resto: trecho, data, página e o link da
 * edição no site do próprio estado.
 *
 * ⚠️ **Estado que não respondeu aparece dizendo por quê** — nunca vira silêncio, que o
 * olho lê como "nada consta".
 */

const DO_GRAU: Record<AchadoEstadual['grau'], { rotulo: string; classe: string }> = {
  confirmado: { rotulo: 'confirmado', classe: 'verde' },
  provavel: { rotulo: 'provável', classe: 'ouro' },
  homonimo: { rotulo: 'de outra pessoa', classe: 'cinza' },
};

const paraDia = (iso: string): string => {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
};

const desdeQuando = (desde: string): string => {
  const [a, m] = desde.split('-');
  return `${m}/${a}`;
};

export function DiariosEstaduais({ investigado }: { investigado: Investigado }) {
  const ehMaster = usarEhMaster();
  const [diarios, setDiarios] = useState<
    Array<{ uf: string; nome: string; desde: string; ligado: boolean; porQueNao: string | null }>
  >([]);
  const [busca, setBusca] = useState<BuscaNosEstados | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [anos, setAnos] = useState<number | null>(5);
  const [aberto, setAberto] = useState(false);
  const [indo, setIndo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const vivo = useRef(true);

  const ler = useCallback(async () => {
    try {
      const [ds, r] = await Promise.all([
        apiInvestigados.diariosEstaduais(),
        apiInvestigados.estadosDaParte(investigado.id),
      ]);
      if (!vivo.current) return;
      setDiarios(ds.diarios);
      setBusca(r.busca);
      /* ⚠️ Os ligados vêm marcados: procurar em quatro estados custa o mesmo que em um. */
      setMarcados(new Set(ds.diarios.filter((d) => d.ligado).map((d) => d.uf)));
    } catch (e) {
      if (vivo.current) setErro(e instanceof Error ? e.message : 'Não consegui ler os diários dos estados.');
    }
  }, [investigado.id]);

  useEffect(() => {
    vivo.current = true;
    void ler();
    return () => {
      vivo.current = false;
    };
  }, [ler]);

  async function procurar() {
    setErro(null);
    setIndo(true);
    try {
      const r = await apiInvestigados.buscarNosEstados(investigado.id, [...marcados], anos);
      setBusca(r.busca);
      setAberto(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui procurar nos estados.');
    } finally {
      setIndo(false);
    }
  }

  const achados = busca?.achados ?? [];
  const confirmados = achados.filter((a) => a.grau === 'confirmado').length;
  const estado = indo
    ? 'procurando nos estados…'
    : busca
      ? `${busca.ufs.length} estado(s) · ${busca.de ? `desde ${busca.de.slice(0, 4)}` : 'todo o acervo'}${achados.length ? ` · ${achados.length} achado(s)` : ' · nada da parte'}`
      : 'ainda não procurado';

  return (
    <div className="imv imv-estados">
      <div className="imv-fontes">
        <span className="imv-rot" title="Os diários oficiais dos estados que o Hub lê">
          Diários dos estados
        </span>
        <span className={`imv-fonte${busca ? (confirmados ? ' ok' : '') : ' falta'}`}>
          <i />
          {estado}
        </span>
        <span className="imv-esp" />
        {ehMaster && (
          <button
            type="button"
            className={`imv-bt fantasma p${aberto ? ' aberto' : ''}`}
            aria-expanded={aberto}
            onClick={() => setAberto(!aberto)}
          >
            <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
            {aberto ? 'Fechar' : busca ? 'Procurar de novo' : 'Procurar nos estados'}
          </button>
        )}
        {!ehMaster && achados.length > 0 && (
          <button type="button" className={`imv-bt fantasma p${aberto ? ' aberto' : ''}`} aria-expanded={aberto} onClick={() => setAberto(!aberto)}>
            {aberto ? 'Fechar' : 'Ver o que apareceu'}
          </button>
        )}
      </div>

      {erro && <p className="erro">{erro}</p>}

      {aberto && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">Diários dos estados · {investigado.nome}</p>
          <p className="ajuda">
            A busca é feita <strong>pelo sistema de cada estado</strong>, pelo nome da parte, e responde em segundos.
            Nada entra no caso sozinho. Cada estado alcança um período diferente, escrito ao lado do nome dele.
          </p>

          {ehMaster && (
            <>
              <ul className="imv-registros">
                {diarios.map((d) => (
                  <li key={d.uf}>
                    <label className="imv-registro-nome imv-marcar">
                      <input
                        type="checkbox"
                        checked={marcados.has(d.uf)}
                        disabled={indo || !d.ligado}
                        onChange={(e) =>
                          setMarcados((antes) => {
                            const novo = new Set(antes);
                            if (e.target.checked) novo.add(d.uf);
                            else novo.delete(d.uf);
                            return novo;
                          })
                        }
                      />
                      {d.nome}
                    </label>
                    {/*
                      ⛔ **O estado desligado aparece, com o porquê.** Sumir com ele faria
                      parecer que o diário não existe — e o que existe é uma decisão, que
                      pode mudar quando a autorização chegar.
                    */}
                    <span className="imv-registro-situacao">
                      {!d.ligado
                        ? (d.porQueNao ?? 'fora por decisão')
                        : busca?.falhas.some((f) => f.uf === d.uf)
                          ? 'não deu para ler'
                          : `busca desde ${desdeQuando(d.desde)}`}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="imv-acoes">
                <label className="imv-dou-periodo">
                  <span className="imv-rotulo">Período</span>
                  <select value={anos ?? 0} onChange={(e) => setAnos(Number(e.target.value) || null)} disabled={indo}>
                    <option value={5}>últimos 5 anos</option>
                    <option value={10}>últimos 10 anos</option>
                    <option value={20}>últimos 20 anos</option>
                    <option value={0}>todo o acervo de cada estado</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="imv-bt primario"
                  disabled={indo || marcados.size === 0}
                  onClick={() => void procurar()}
                >
                  {indo ? 'Procurando…' : `Procurar em ${marcados.size} estado(s)`}
                </button>
              </div>
            </>
          )}

          {busca?.falhas.length ? (
            <div className="aviso-atencao">
              {busca.falhas.map((f) => (
                <p key={f.uf}>{f.porQue}</p>
              ))}
            </div>
          ) : null}

          {busca && achados.length === 0 && !indo && (
            <p className="ajuda">
              Nada da parte nos estados procurados, no período escolhido. Isso não quer dizer que não exista: quer
              dizer que não consta ali.
            </p>
          )}

          {achados.length > 0 && (
            <ul className="imv-lista imv-dou-lista">
              {achados.map((a, i) => (
                <li key={`${a.uf}-${a.data}-${a.pagina}-${i}`} className="imv-linha">
                  <div className="imv-linha-topo">
                    <span className={`imv-tag ${DO_GRAU[a.grau].classe}`}>{DO_GRAU[a.grau].rotulo}</span>
                    <b>{a.nomeDoDiario}</b>
                    <span className="imv-esp" />
                    <span className="dado">{paraDia(a.data)}</span>
                  </div>
                  <div className="imv-detalhe-texto">
                    <p className="imv-legenda">{a.pagina ? `página ${a.pagina}` : 'página não informada'}</p>
                    {a.trechos.map((t, j) => (
                      <p key={j} className="imv-dou-trecho">
                        {t}
                      </p>
                    ))}
                    <p className="imv-legenda">
                      {a.porQue}
                      {a.enderecoNoDiario && (
                        <>
                          {' · '}
                          <a href={a.enderecoNoDiario} target="_blank" rel="noreferrer noopener">
                            ver a edição no diário do estado
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
